import React, { useCallback, useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Link } from "react-router-dom";
import PrimaryButton from "../components/PrimaryButton";
import useDataFetch from "../hooks/useDataFetch";
import { MobileResponsive } from "./MobileResponsive";
import { useDispatch } from "react-redux";
import { deleteAdminUser } from "../features/superAdminSlice";
import toast from "react-hot-toast";
import useRecommendations from "../hooks/useRecommendations";
import RecommendationsList from "../components/RecommendationsList";
import FloatingInput from "../components/FloatingInput";
import { otpService } from "../features/authSlice";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const pageSize = 10;

const ManageAdmin = () => {
  const dispatch = useDispatch();
  const { data, isLoading, mutate } = useDataFetch("/admin/admins", null, {
    revalidateOnFocus: true,
  });

  const { data: user, isLoading: isLoading2 } = useDataFetch(`/admin/profile`);

  const [currentPage, setCurrentPage] = useState(1);
  const [inputStates, setInputStates] = useState({
    searchAdmin: { isFocused: false, isFilled: false, value: "" },
  });
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({
    otp: "",
  });

  const [deleteAdmin, setDeleteAdmin] = useState({
    deleteAdmin: false,
    data: {},
  });

  const {
    recommendations,
    highlightedIndex,
    setHighlightedIndex,
    filterRecommendations,
    handleKeyDown,
    isVisible,
    setIsVisible,
  } = useRecommendations(data?.data, "name");

  const [sidebars, setSidebars] = useState({
    deleteAdmin: false,
  });

  const sidebarRefs = {
    deleteAdmin: useRef(null),
  };

  useEffect(() => {
    if (data?.data) {
      setFilteredAdmins(data?.data);
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };

    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebars]);

  const handleOtpBlur = useCallback(() => {
    if (!otp || otp.length !== 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: "OTP should be 5 digits long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, otp: "" }));
    }
  }, [otp]);

  const handleInputChange = (event, inputField) => {
    const value = event.target.value;

    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: {
        ...prevState[inputField],
        value: value,
        isFilled: value !== "",
      },
    }));

    if (inputField === "searchAdmin") {
      if (value.trim() === "") {
        setFilteredAdmins(data?.data);
        filterRecommendations("");
        setIsVisible(false); // Hide recommendations if the input is cleared
      } else {
        filterRecommendations(value);
      }
    }
  };

  const handleRecommendationClick = (recommendation) => {
    const filtered = data?.data.filter((admin) =>
      admin?.name.toLowerCase().includes(recommendation.toLowerCase())
    );
    setFilteredAdmins(filtered);
    setInputStates((prevState) => ({
      ...prevState,
      searchAdmin: { ...prevState.searchAdmin, value: recommendation },
    }));
    setIsVisible(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleFocus = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: true },
    }));
  };

  const handleBlur = (inputField) => {
    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: { ...prevState[inputField], isFocused: false },
    }));
  };

  const handleSendOtp = async () => {
    if (!isLoading2) {
      const resultAction = await dispatch(otpService({ email: user?.email }));

      if (otpService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);

        setDeleteAdmin((prevState) => ({
          ...prevState,
          deleteAdmin: true,
        }));
      }
    }
  };

  const handleDeleteAdmin = async () => {
    const resultAction = await dispatch(
      deleteAdminUser({
        adminId: deleteAdmin.data._id,
        otp: otp,
      })
    );

    if (deleteAdminUser.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      mutate();
      handleCloseSidebar();
    }
  };

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    setSidebars({
      deleteDriver: false,
    });
  };

  // Calculate the admin to display on the current page
  const currentAdmins =
    !isLoading &&
    filteredAdmins.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalAdminsCount = !isLoading && filteredAdmins.length;

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Efficient Admin Management"
        pageDescription="Empower your team with Beep! Easily add or remove admins to ensure optimal management of your logistics operations and enhance efficiency."
        pageKeywords="Beep, admin management, add admin, delete admin, team empowerment, logistics management"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between w-full">
            <button disabled className="flex items-center gap-3">
              {/* <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div> */}
              <span className="font-semibold text-[20px]">Manage Admin</span>
            </button>

            <div className="flex items-center">
              <form
                action=""
                className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] w-[27vw]"
                onSubmit={handleSubmit}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search admin"
                    className={`peer rounded-full py-1.5 text-[#F7631D] placeholder:text-transparent text-base font-medium w-full outline-none px-1 placeholder:select-none`}
                    value={inputStates.searchAdmin.value}
                    onInput={(e) => handleInputChange(e, "searchAdmin")}
                    onFocus={() => handleFocus("searchAdmin")}
                    onBlur={() => handleBlur("searchAdmin")}
                    onKeyDown={(e) =>
                      handleKeyDown(e, handleRecommendationClick)
                    }
                  />
                  <label
                    className={`absolute left-1 transform transition-all duration-200 ${
                      inputStates.searchAdmin.isFocused ||
                      inputStates.searchAdmin.isFilled
                        ? "-top-0.5 -translate-y-1/2 left-2 text-xs px-1 bg-white rounded-full"
                        : "top-1/2 -translate-y-1/2 left-3 text-base font-medium"
                    } text-[#F7631D] pointer-events-none`}
                  >
                    Search admin
                  </label>

                  {isVisible && (
                    <RecommendationsList
                      recommendations={recommendations}
                      handleRecommendationClick={handleRecommendationClick}
                      highlightedIndex={highlightedIndex}
                      setHighlightedIndex={setHighlightedIndex}
                      setIsVisible={setIsVisible}
                    />
                  )}
                </div>
                <button type="submit">
                  <img src="/assets/search.svg" alt="Search" />
                </button>
              </form>
            </div>
            <div className="flex items-center gap-4 text-[#413F3F]">
              <span className="font-normal 2xl:text-base xl:text-sm lg:text-sm flex items-center gap-1">
                Total: <span className="font-bold">{totalAdminsCount}</span>
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-[80dvh]">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          ) : (
            <>
              <div className="w-full rounded-lg grid grid-cols-3 gap-3 py-8">
                {currentAdmins.map((admin) => (
                  <div className="bg-[#FFFFFF] rounded-lg flex items-center justify-between border-[0.5px] border-[#00000014] shadow-custom4 px-4 py-2 w-full">
                    <div className="flex items-center gap-5 w-72">
                      {/* <img
                        src="/assets/user.png"
                        alt="admin img"
                        className="h-14"
                      /> */}

                      <Avatar
                        name={admin?.name}
                        size="52"
                        round={true}
                        maxInitials={2}
                        textSizeRatio={3}
                        style={{ letterSpacing: "1px" }}
                      />
                      <div className="flex flex-col 2xl:gap-1 xl:gap-2 lg:gap-2 w-full">
                        <p className="2xl:text-[17px] xl:text-[16px] lg:text-[15px] font-semibold tracking-wide capitalize">
                          {admin?.name}
                        </p>
                        <p
                          className={`2xl:text-[13px] xl:text-[11px] lg:text-[9.5px] font-semibold tracking-wide flex items-center 2xl:mb-[0px] xl:mb-[0px] lg:mb-[0px] w-full`}
                        >
                          <span className="pt-1 break-words w-full -mt-2.5 text-[#8D8D8D]">
                            {admin?.email}
                          </span>
                        </p>
                      </div>
                    </div>
                    <img
                      src="/assets/bin.svg"
                      alt=""
                      className="h-[20px] cursor-pointer"
                      onClick={() => {
                        toggleSidebar("deleteAdmin", true);
                        setDeleteAdmin((prevState) => ({
                          ...prevState,
                          data: admin,
                        }));
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center py-3 w-52 m-auto">
                <PrimaryButton
                  text="Add new admin"
                  uppercase={false}
                  path="/add-admin"
                  width="w-full"
                />
              </div>
            </>
          )}

          {/* Delete Admin Sidebar */}
          <div
            ref={sidebarRefs.deleteAdmin}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.deleteAdmin ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">
                    Delete admin
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={deleteAdmin?.data?.name}
                  className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={deleteAdmin?.data?.name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {deleteAdmin?.data?.name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="2xl:text-[18px] xl:text-[18px] lg:text-[16px] font-semibold w-full">
                  {deleteAdmin?.data?.email}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Contact"}
                    path={`mailto:${deleteAdmin?.data?.email}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <div className="2xl:text-lg xl:text-lg lg:text-[16px]">
                  {!deleteAdmin.deleteAdmin ? (
                    <>
                      <p>
                        This account is scheduled for permanent deletion. If you
                        wish to proceed with deleting the account{" "}
                        <strong className="capitalize">
                          {data?.data?.name}
                        </strong>
                        , please request a one-time password (OTP) for
                        confirmation.
                      </p>

                      <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-10">
                        <PrimaryButton
                          text="Request a OTP"
                          width="w-full"
                          uppercase={false}
                          event={handleSendOtp}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <form
                        action=""
                        className="w-full"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="py-5 flex flex-col gap-5">
                          <label
                            htmlFor="OTP"
                            className="2xl:text-[17px] xl:text-[15px] lg:text-[14px] font-semibold text-[#413F3F]"
                          >
                            Please enter the OTP sent to your email address
                          </label>
                          <FloatingInput
                            name="otp"
                            id="otp"
                            type="number"
                            placeholder="Enter OTP"
                            value={otp}
                            error={errors.otp}
                            handleBlur={handleOtpBlur}
                            onChange={(e) => setOtp(e.target.value)}
                          />
                        </div>

                        <div className="w-full m-auto flex flex-col justify-center gap-5 pt-5">
                          <PrimaryButton
                            text="Confirm"
                            width="w-full"
                            type="submit"
                            uppercase={false}
                            event={handleDeleteAdmin}
                          />
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageAdmin;
